import { useForm } from 'react-hook-form'
import './LessonPlanForm.scss'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  createLessonPlanSchema,
  updateLessonPlanSchema
} from '../../../../schemas/lessonPlanSchema'
import { useEffect } from 'react'
import { useState } from 'react'
import LessonPlansService from '../../../../services/lessonPlans.service'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'

const LessonPlanForm = ({
  book,
  lessonPlan,
  setLoadedLessonPlans,
  setLoadedBook
}) => {
  const MySwal = withReactContent(Swal)
  const [chaptersWithLessonPlan, setChaptersWithLessonPlan] = useState([])

  const { handleSubmit, register, reset } = useForm({
    resolver: zodResolver(
      lessonPlan ? createLessonPlanSchema : updateLessonPlanSchema
    )
  })

  useEffect(() => {
    if (book) {
      const chaptersWithLessons = book.lessonPlans.map((plan) => plan.chapter)
      setChaptersWithLessonPlan(chaptersWithLessons)
    }

    if (lessonPlan) {
      const values = {
        chapter: lessonPlan.chapter,
        presentationLink: lessonPlan.presentationLink || undefined,
        videoLink: lessonPlan.videoLink || undefined
      }

      reset(values)
    }
  }, [book, lessonPlan, reset])

  async function handleForm(data) {
    if (lessonPlan) {
      const changedData = checkIfDataHasChanged(data)
      if (!changedData) {
        MySwal.fire({
          confirmButtonColor: '#87BF40',
          confirmButtonText: <span>Ok</span>,
          icon: 'info',
          title: (
            <span style={{ color: '#FFC107' }}>Nenhum campo foi editado</span>
          )
        })
      } else {
        await updateLesson(changedData)
      }
    } else {
      await createLesson(data)
    }
  }

  function checkIfDataHasChanged(formData) {
    const changedFields = {}
    let hasChanges = false

    Object.keys(formData).forEach((key) => {
      if (formData[key] !== lessonPlan[key]) {
        changedFields[key] = formData[key]
        hasChanges = true
      }
    })

    return !hasChanges ? null : changedFields
  }

  async function createLesson(data) {
    try {
      let newLessonPlan = await LessonPlansService.create(book.id, data)
      newLessonPlan = {
        id: newLessonPlan,
        ...data,
        createdAt: Date.now().toString(),
        updatedAt: Date.now().toString(),
        deletedAt: null
      }

      setLoadedLessonPlans((prev) => [...prev, newLessonPlan])
      setLoadedBook((prev) => ({
        ...prev,
        lessonPlans: [...prev.lessonPlans, newLessonPlan]
      }))

      MySwal.fire({
        confirmButtonColor: '#87BF40',
        confirmButtonText: <span>Ok</span>,
        icon: 'success',
        title: (
          <span style={{ color: '#FFC107' }}>
            Plano de aula criado com sucesso!
          </span>
        )
      })
    } catch (error) {
      MySwal.fire({
        confirmButtonColor: '#87BF40',
        confirmButtonText: <span>Voltar</span>,
        icon: 'error',
        title: (
          <span style={{ color: '#FFC107' }}>
            Não foi possível criar o plano de aula!
          </span>
        )
      })
    }
  }

  async function updateLesson(data) {
    try {
      await LessonPlansService.update(lessonPlan.id, data)

      setLoadedLessonPlans((prev) =>
        prev.map((lesson) =>
          lesson.id === lessonPlan.id ? { ...lesson, ...data } : lesson
        )
      )

      setLoadedBook((prev) => ({
        ...prev,
        lessonPlans: prev.lessonPlans.map((lesson) =>
          lesson.id === lessonPlan.id ? { ...lesson, ...data } : lesson
        )
      }))

      MySwal.fire({
        confirmButtonColor: '#87BF40',
        confirmButtonText: <span>Ok</span>,
        icon: 'success',
        title: (
          <span style={{ color: '#FFC107' }}>
            Plano de aula editado com sucesso!
          </span>
        )
      })
    } catch (error) {
      MySwal.fire({
        confirmButtonColor: '#87BF40',
        confirmButtonText: <span>Voltar</span>,
        icon: 'error',
        title: (
          <span style={{ color: '#FFC107' }}>
            Não foi possível editar o plano de aula!
          </span>
        )
      })
    }
  }

  return (
    <form
      className='lesson-plan-form'
      onSubmit={handleSubmit(handleForm)}
    >
      <div className='book-form-fields'>
        <div className='form-group'>
          <label>
            Capítulo:<span className='highlighter'>*</span>
          </label>
          <select
            {...register('chapter')}
            className='form-control'
          >
            <option
              hidden
              value=''
            >
              Selecione um capítulo
            </option>
            {[...Array(book.numberOfChapters)].map((_, index) => {
              const chapterNumber = index + 1
              const hasLessonPlans =
                chaptersWithLessonPlan.includes(chapterNumber)

              return (
                <option
                  key={chapterNumber}
                  value={chapterNumber}
                  disabled={hasLessonPlans}
                >{`Capítulo ${chapterNumber}`}</option>
              )
            })}
          </select>
        </div>
        <div className='form-group'>
          <label>Link da apresentação:</label>
          <input
            {...register('presentationLink')}
            type='text'
            className='form-control'
          />
        </div>
        <div className='form-group'>
          <label>Link do vídeo:</label>
          <input
            {...register('videoLink')}
            type='text'
            className='form-control'
          />
        </div>
        <div className='lesson-plan-form-actions'>
          <button
            type='submit'
            className='button-full btn-save add-lesson-plan-button'
          >
            {lessonPlan ? 'Editar' : 'Criar'}
          </button>
        </div>
      </div>
    </form>
  )
}

export default LessonPlanForm
